import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Button from '@/components/molecules/button/button';
import MultiSelectDropdown from '@/components/molecules/multi-select-dropdown/multi-select-dropdown';
import FilterChipList, {
  type FilterChip,
} from '@/components/organisms/filter-chip-list/filter-chip-list';
import Link from '@/components/utilities/link/link';
import { useHotelContext } from '@/libraries/contexts/hotel-context/hotel-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePredefinedSearchContext } from '@/libraries/contexts/predefined-search-context/predefined-search-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import RoomMatrixLarge from './room-matrix-large/room-matrix-large';
import RoomMatrixSmall from './room-matrix-small/room-matrix-small';
import {
  filterRowsByRoomTypes,
  getRoomTypes,
  type RoomMatrixRoomType,
} from './room-matrix.utils';
import styles from './room-matrix.module.scss';

interface RoomTypeFilterChip extends FilterChip {
  roomTypeId: RoomMatrixRoomType['id'];
}

export interface HubspotFormData {
  departureDate: string | null;
  departureName: string | null;
  destinationName: string | null;
  hotelName: string | null;
  selectedPaxDistribution: string | null;
}

export interface RoomMatrixProps {
  /** Indicates whether to show hubspot form for inquiries */
  enableHubspotform?: Content.Components['RoomMatrixModuleDto']['enableHubspotform'];
  /** Indicates whether to show the room type filter */
  enableRoomTypeFilter?: Content.Components['RoomMatrixModuleDto']['enableRoomTypeFilter'];
  /** The facilities in the room matrix */
  facilitiesInRoom?: Content.Components['RoomMatrixModuleDto']['facilitiesInRoom'];
  /** The headline to show */
  headline?: Content.Components['RoomMatrixModuleDto']['headline'];
  /** Whether the room matrix is in the configuration flow */
  isInConfigurationFlow?: boolean;
  /** Whether the room matrix is loading */
  isLoading?: boolean;
  /** The link to show */
  link?: Content.Components['RoomMatrixModuleDto']['link'];
  /** The room matrix to show */
  roomMatrix?: Content.Components['RoomMatrixModuleDto']['roomMatrix'];
}

// Keep it 3, it's just for readability
export const COLUMN_LIMIT = 3;
export const ROW_LIMIT = 4;
export const LOADING_ROW_LIMIT = 3;

const RoomMatrix = ({
  enableHubspotform = false,
  enableRoomTypeFilter = false,
  headline,
  isLoading = false,
  isInConfigurationFlow = false,
  link,
  facilitiesInRoom = [],
  roomMatrix,
}: RoomMatrixProps) => {
  const { t } = useI18n();
  const searchParams = useSearchParams();
  const { setSelectedRoom, setBookingUrl, setCurrentStepSummary, setUniqueId } =
    useHotelContext();
  const { offerId } = usePredefinedSearchContext();

  const getStartIndex = () => {
    if (!roomMatrix) return 0;

    const activeColumnIndex = roomMatrix.columns.findIndex(
      column => column.isSelected,
    );

    if (activeColumnIndex <= 1) return 0;

    if (activeColumnIndex >= roomMatrix.columns.length - COLUMN_LIMIT)
      return roomMatrix.columns.length - COLUMN_LIMIT;

    // -1 removed so the offer is centered
    return activeColumnIndex - 1;
  };
  const [startIndex, setStartIndex] = useState(getStartIndex());
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [selectedRoomTypeIds, setSelectedRoomTypeIds] = useState<
    RoomMatrixRoomType['id'][]
  >([]);

  const roomTypes = useMemo(
    () => getRoomTypes(roomMatrix?.rows),
    [roomMatrix?.rows],
  );

  // Ignore selected room types that are no longer part of the room matrix, e.g. after changing date
  const activeRoomTypes = useMemo(
    () =>
      roomTypes.filter(roomType => selectedRoomTypeIds.includes(roomType.id)),
    [roomTypes, selectedRoomTypeIds],
  );

  const filteredRoomMatrix = useMemo(() => {
    if (!roomMatrix || activeRoomTypes.length === 0) return roomMatrix;

    return {
      ...roomMatrix,
      rows: filterRowsByRoomTypes(
        roomMatrix.rows,
        activeRoomTypes.map(roomType => roomType.id),
      ),
    };
  }, [roomMatrix, activeRoomTypes]);

  // The number of combinations each room type is part of within the current filter result
  const filteredRoomTypeCounts = useMemo(
    () =>
      new Map(
        getRoomTypes(filteredRoomMatrix?.rows).map(roomType => [
          roomType.id,
          roomType.count,
        ]),
      ),
    [filteredRoomMatrix?.rows],
  );

  const roomTypeFilterItems = roomTypes.map(roomType => {
    const count = filteredRoomTypeCounts.get(roomType.id) ?? 0;

    return {
      // Selected room types stay enabled so they can always be deselected
      isDisabled: count === 0 && !activeRoomTypes.includes(roomType),
      label: roomType.name,
      subText: t(
        `items.modules.roomMatrix.roomTypeCombinations_${count === 1 ? 'one' : 'other'}`,
        { count },
      ),
      value: roomType.id,
    };
  });

  const roomTypeFilterChips: RoomTypeFilterChip[] = activeRoomTypes.map(
    roomType => ({
      id: String(roomType.id),
      label: roomType.name,
      roomTypeId: roomType.id,
    }),
  );

  const showRoomTypeFilter = enableRoomTypeFilter && roomTypes.length > 1;

  const handleRemoveRoomTypeChip = (chip: RoomTypeFilterChip) => {
    setSelectedRoomTypeIds(previous =>
      previous.filter(roomTypeId => roomTypeId !== chip.roomTypeId),
    );
  };

  // In the configuration flow the selection is the chosen offer, otherwise the row marked as selected
  const selectedRow = roomMatrix?.rows.find(row =>
    isInConfigurationFlow
      ? row.cells.some(cell => !!offerId && cell.offerId === offerId)
      : row.isSelected,
  );

  const isSelectedRowHidden =
    !!selectedRow && !filteredRoomMatrix?.rows.includes(selectedRow);

  // Removes the room types not part of the selected combination from the filter, so it is shown without clearing the whole filter
  const handleShowSelectedRow = () => {
    if (!roomMatrix || !selectedRow) return;

    const roomTypeIds = activeRoomTypes
      .map(roomType => roomType.id)
      .filter(roomTypeId =>
        selectedRow.rooms.some(room => room.roomTypeId === roomTypeId),
      );

    const selectedRowIndex = filterRowsByRoomTypes(
      roomMatrix.rows,
      roomTypeIds,
    ).indexOf(selectedRow);

    setSelectedRoomTypeIds(roomTypeIds);

    if (selectedRowIndex >= ROW_LIMIT) setIsCollapsed(true);
  };

  useEffect(() => {
    setStartIndex(getStartIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, roomMatrix]);

  const isLarge = useWindowSize('desktop');

  const handlePrevious = () => {
    setStartIndex(previous => {
      const newIndex = previous - 1;

      if (newIndex < 0) return 0;

      return newIndex;
    });
  };

  const handleNext = () => {
    setStartIndex(previous => {
      if (!roomMatrix) return 0;

      const newIndex = previous + 1;

      if (newIndex > roomMatrix.columns.length - COLUMN_LIMIT)
        return roomMatrix.columns.length - COLUMN_LIMIT;

      return newIndex;
    });
  };

  const getSelectedRoom = (
    rows: Content.Components['RoomMatrixRowDto'][],
  ): Content.Components['RoomMatrixRoomDto'] | null => {
    if (!rows) return null;

    const selectedRow = rows.find(row => row.isSelected);

    if (!selectedRow) return null;

    const selectedRoom = selectedRow.rooms.find(room => !!room);

    return selectedRoom ?? null;
  };

  const getSelectedCell = (
    rows: Content.Components['RoomMatrixRowDto'][],
  ): Content.Components['RoomMatrixCellDto'] | null => {
    if (!rows) return null;

    const selectedRow = rows.find(row => row.isSelected);

    if (!selectedRow) return null;

    const selectedCell = selectedRow.cells.find(cell => cell.isSelected);

    return selectedCell ?? null;
  };

  const roomTypeFilter = showRoomTypeFilter ? (
    <div className={styles.roomTypeFilter}>
      <MultiSelectDropdown
        className={styles.roomTypeDropdown}
        icon="Filter"
        items={roomTypeFilterItems}
        label={t('items.modules.roomMatrix.roomTypeFilter')}
        onChange={setSelectedRoomTypeIds}
        values={activeRoomTypes.map(roomType => roomType.id)}
      />

      <FilterChipList
        className={styles.roomTypeChips}
        clearLabel={t('items.search.filters.clearAllFilters')}
        items={roomTypeFilterChips}
        onClickRemoveAll={() => setSelectedRoomTypeIds([])}
        onClickRemoveChip={handleRemoveRoomTypeChip}
      />

      {isSelectedRowHidden && (
        <AlertBanner variant="attention">
          <div className={styles.roomTypeAlert}>
            <Typography>
              {t('items.modules.roomMatrix.roomTypeFilterHidesSelection')}
            </Typography>

            <Link onClick={handleShowSelectedRow}>
              <Typography className={styles.roomTypeAlertLink} tag="span">
                {t('items.modules.roomMatrix.roomTypeFilterShowSelection')}
              </Typography>
            </Link>
          </div>
        </AlertBanner>
      )}
    </div>
  ) : undefined;

  const matrixProps = {
    columns: roomMatrix?.columns,
    facilitiesInRoom,
    headline,
    isCollapsed,
    isInConfigurationFlow,
    isLoading,
    link,
    onNext: handleNext,
    onPrevious: handlePrevious,
    roomMatrix: filteredRoomMatrix,
    roomTypeFilter,
    rows: filteredRoomMatrix?.rows,
    startIndex,
  };

  useEffect(() => {
    if (!roomMatrix?.rows) return;

    const selectedRoom = getSelectedRoom(roomMatrix.rows);
    const selectedCell = getSelectedCell(roomMatrix.rows);

    setSelectedRoom(selectedRoom?.name ?? undefined);
    setUniqueId(selectedCell?.favoriteId ?? '');

    if (isInConfigurationFlow) {
      setBookingUrl(selectedCell?.bookingUrl ?? undefined);
      setCurrentStepSummary(selectedRoom?.name ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomMatrix?.rows]);

  return (
    <div className={styles.component}>
      {isLarge ? (
        <RoomMatrixLarge
          {...matrixProps}
          enableHubspotform={enableHubspotform}
        />
      ) : (
        <RoomMatrixSmall
          {...matrixProps}
          enableHubspotform={enableHubspotform}
        />
      )}

      {filteredRoomMatrix && filteredRoomMatrix.rows.length > ROW_LIMIT && (
        <div className={styles.collapse}>
          <Button
            onClick={() => setIsCollapsed(previous => !previous)}
            text={
              isCollapsed
                ? t('items.common.words.showLess')
                : t('items.common.words.showMoreOptions', {
                    options: filteredRoomMatrix.rows.length - ROW_LIMIT,
                  })
            }
            variant="secondary"
          />
        </div>
      )}
    </div>
  );
};

RoomMatrix.displayName = 'RoomMatrix';

export default RoomMatrix;
